import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Share, Plus, Smartphone, Apple, Check } from "lucide-react";

export default function InstallApp() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <div className="relative z-10 min-h-screen" data-testid="page-instalar">
      <header className="border-b border-white/10 bg-[#05070f]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/cyvr-logo.png" alt="CYVR" className="h-9 w-9 object-contain drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
            <span className="font-head text-sm font-bold tracking-tight text-white">CYVR<span className="text-cyber-cyan"> LABS</span></span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-cyber-cyan">
            <ArrowLeft className="h-4 w-4" /> Inicio
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
        <div className="flex flex-col items-center text-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            src="/icons/icon-512.png"
            alt="Icono de la app CYVR Labs"
            data-testid="install-icon"
            className="h-28 w-28 rounded-[22px] border border-white/10 shadow-[0_0_40px_rgba(0,240,255,0.25)]"
          />
          <p className="mt-6 font-mono-tech text-[11px] uppercase tracking-[0.28em] text-cyber-cyan">App móvil</p>
          <h1 className="mt-3 font-head text-4xl font-bold tracking-tight text-white sm:text-5xl">Instala CYVR Labs</h1>
          <p className="mt-4 max-w-xl font-sans text-base leading-relaxed text-white/60">
            Añade CYVR Labs a la pantalla de inicio de tu móvil y ábrela como una app nativa, a pantalla completa. No ocupa apenas espacio y se actualiza sola.
          </p>

          {installed ? (
            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-cyber-cyan/40 bg-cyber-cyan/10 px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-cyber-cyan" data-testid="install-done">
              <Check className="h-4 w-4" /> App instalada
            </div>
          ) : deferred ? (
            <button
              onClick={install}
              data-testid="install-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-7 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#05070f] transition-transform hover:scale-[1.04]"
            >
              <Download className="h-4 w-4" /> Instalar aplicación
            </button>
          ) : (
            <p className="mt-8 max-w-md font-sans text-sm text-white/50" data-testid="install-hint">
              Abre esta página <b className="text-white/80">desde el navegador de tu móvil</b> para instalarla. Sigue los pasos según tu dispositivo 👇
            </p>
          )}
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="install-ios">
            <div className="flex items-center gap-3">
              <Apple className="h-6 w-6 text-white" />
              <h2 className="font-head text-lg font-bold text-white">iPhone / iPad (Safari)</h2>
            </div>
            <ol className="mt-5 space-y-3 font-sans text-sm text-white/70">
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">1</span> Abre <b className="text-white">cyvrlabs.com</b> en Safari.</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">2</span> Pulsa el botón <b className="inline-flex items-center gap-1 text-white"><Share className="h-3.5 w-3.5" /> Compartir</b> (abajo).</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">3</span> Elige <b className="inline-flex items-center gap-1 text-white"><Plus className="h-3.5 w-3.5" /> Añadir a pantalla de inicio</b>.</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">4</span> Confirma <b className="text-white">Añadir</b>. ¡Listo, verás el icono de CYVR!</li>
            </ol>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="install-android">
            <div className="flex items-center gap-3">
              <Smartphone className="h-6 w-6 text-cyber-cyan" />
              <h2 className="font-head text-lg font-bold text-white">Android (Chrome)</h2>
            </div>
            <ol className="mt-5 space-y-3 font-sans text-sm text-white/70">
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">1</span> Abre <b className="text-white">cyvrlabs.com</b> en Chrome.</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">2</span> Pulsa el botón <b className="text-white">Instalar aplicación</b> de arriba, o el menú <b className="text-white">⋮</b>.</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">3</span> Elige <b className="text-white">Instalar app / Añadir a pantalla de inicio</b>.</li>
              <li className="flex gap-3"><span className="font-mono-tech text-cyber-cyan">4</span> Confirma. El icono de CYVR aparecerá en tu pantalla.</li>
            </ol>
          </div>
        </div>

        <p className="mt-10 text-center font-sans text-xs text-white/40">
          Nota: es una app web instalable (PWA). No está en App Store ni Google Play, se instala directamente desde el navegador y no requiere descargas ni cuentas.
        </p>
      </main>
    </div>
  );
}
